import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  users: { is_fetch: false, data: null },
  sizes: { is_fetch: false, data: null },
  services: { is_fetch: false, data: null },
  parties: { is_fetch: false, data: null },
  priceList: { is_fetch: false, data: null },
  conversionRate: { is_fetch: false, data: null },
  submission: {
    is_fetch: false,
    data: [],
    total: null,
    pages: [],
  },
  result: { is_fetch: false, data: [], total: null, pages: [] },
  reply: { is_fetch: false, data: [], total: null, pages: [] },
  return: { is_fetch: false, data: [], total: null, pages: [] },
  bill: { is_fetch: false, data: [], total: null, pages: [] },
  creditnote: { is_fetch: false, data: [], total: null, pages: [] },
};

export const labSlice = createSlice({
  name: "lab",
  initialState,
  reducers: {
    get: (state, { payload }) => {
      const { name, data, total, fetch, pages } = payload;
      state[name] = {
        is_fetch: fetch,
        data: data,
        total: total || null,
        pages: pages ? pages : state[name]?.pages,
      };
    },
    insert: (state, { payload }) => {
      const { name, data = [], fetch } = payload;
      if (!state[name].data) {
        state[name].data = [];
      }
      const items = Array.isArray(data) ? data : [data];
      state[name].data.push(...items);
      state[name].total = (state[name].total || 0) + items.length;
      state[name].is_fetch = fetch;
    },
    update: (state, { payload }) => {
      const { name, data, fetch, key } = payload;
      if (!state[name]?.data) {
        state[name].data = [];
      }
      const items = Array.isArray(data) ? data : [data];
      items.forEach((updatedItem) => {
        const index = state[name].data?.findIndex(
          (item) => item[key] === updatedItem[key]
        );

        if (index !== -1) {
          // ✅ Record exists → update karo
          state[name].data[index] = updatedItem;
        } else {
          // ✅ Record nahi mila → insert karo + total badhao

          state[name].data.push(updatedItem);
          state[name].total = (state[name].total || 0) + 1;
        }
      });

      // Sorting (based on key)
      state[name].data.sort((a, b) => {
        if (a[key] < b[key]) return -1;
        if (a[key] > b[key]) return 1;
        return 0;
      });
      state[name].is_fetch = fetch ? fetch : state[name].is_fetch;
    },
    remove: (state, { payload }) => {
      const { name, data, fetch, key } = payload;
      const items = Array.isArray(data) ? data : [data];
      const keysToRemove = items?.map((item) => item[key]);
      state[name].data = state[name].data.filter(
        (item) => !keysToRemove.includes(item[key])
      );
      state[name].data.sort((a, b) => {
        if (a[key] < b[key]) return -1;
        if (a[key] > b[key]) return 1;
        return 0;
      });
      state[name].is_fetch = fetch ?? state[name].is_fetch;
      state[name].total = Math.max((state[name].total || 0) - items.length, 0);
    },

    transaction: (state, { payload }) => {
      const {
        name,
        data,
        total,
        next_last_id,
        key,
        page,
        sortKey = key,
      } = payload;

      if (!state[name]?.data) {
        state[name].data = [];
      }

      const items = Array.isArray(data) ? data : [data];
      // ✅ Insert or update data
      items.forEach((newItem) => {
        const index = state[name].data.findIndex(
          (item) => item[key] === newItem[key]
        );
        if (index !== -1) {
          // Update existing
          state[name].data[index] = newItem;
        } else {
          // Insert new
          state[name].data.push(newItem);
        }
      });

      // ✅ Sort data (ascending by sortKey)
      state[name].data.sort((a, b) => {
        if (a[sortKey] < b[sortKey]) return -1;
        if (a[sortKey] > b[sortKey]) return 1;
        return 0;
      });

      // ✅ Maintain pages as array of objects and sort
      if (!state[name].pages) {
        state[name].pages = [];
      }

      if (page !== undefined) {
        const pageIndex = state[name].pages.findIndex((p) => p.page === page);

        if (pageIndex !== -1) {
          // Update existing page with new next_last_id
          state[name].pages[pageIndex] = {
            page,
            next_last_id:
              next_last_id ?? state[name].pages[pageIndex].next_last_id,
          };
        } else {
          // Insert new page object
          state[name].pages.push({
            page,
            next_last_id: next_last_id ?? null,
          });
        }

        // sort pages by page number ascending
        state[name].pages.sort((a, b) => a.page - b.page);
      }
      // ✅ Update total (keep this separate as it's not page-specific)
      state[name].total = total ?? state[name].total;
      state[name].is_fetch = true;
    },

    // ✅ Batch Update
    batchUpdate: (state, { payload }) => {
      const {
        name,
        insert = [],
        updateItems = [],
        removeItems = [],
        key = "id",
        fetch = true,
      } = payload;

      if (!state[name].data) {
        state[name].data = [];
      }

      // Insert
      const insertArr = Array.isArray(insert) ? insert : [insert];
      if (insertArr.length) {
        state[name].data.push(...insertArr);
        state[name].total = (state[name].total || 0) + insertArr.length;
      }

      // Update
      const updateArr = Array.isArray(updateItems)
        ? updateItems
        : [updateItems];

      if (updateArr?.length) {
        updateArr.forEach((updatedItem) => {
          const index = state[name].data.findIndex(
            (item) => item[key] === updatedItem[key]
          );
          if (index !== -1) {
            // Record mila → replace karo
            state[name].data[index] = updatedItem;
          } else {
            // Record nahi mila → insert karo aur total increase karo
            state[name].data.push(updatedItem);
            state[name].total = (state[name].total || 0) + 1;
          }
        });
      }
      // Remove
      const removeArr = Array.isArray(removeItems)
        ? removeItems
        : [removeItems];
      if (removeArr.length) {
        const keysToRemove = removeArr.map((item) => item[key]);
        state[name].data = state[name].data.filter(
          (item) => !keysToRemove.includes(item[key])
        );
        state[name].total = Math.max(
          (state[name].total || 0) - removeArr.length,
          0
        );
      }
      state[name].is_fetch = fetch;
    },
    reset_state: () => initialState,
    // Additional action to set is_fetch to false when starting a fetch
    startFetch: (state, { payload }) => {
      const { name } = payload;
      state[name].is_fetch = false;
    },
  },
});

export const {
  get,
  insert,
  update,
  remove,
  reset_state,
  startFetch,
  transaction,
  batchUpdate,
} = labSlice.actions;
export default labSlice?.reducer;
